/**
 * The boundary between a WYSIWYG editor's DOM and the markup a message actually
 * carries (see `richText.ts`). Both directions live here, and they are a pair:
 * `serializeToMarkup` reads the DOM out, `fillFromMarkup` writes a draft back
 * in, and each is only correct in terms of the other.
 *
 * The editor is contenteditable — bold looks bold while it is being typed —
 * but a message is a plain string end to end, so the DOM is serialised on
 * every keystroke and the HTML never leaves the editor. Anything the browser
 * produced that this subset has no word for (a colour, an underline, a pasted
 * table) loses its formatting and keeps its words, which is the right way round.
 */

import { parseRichText, type Inline } from './richText.js';

/** Anything that owns a line of its own, rather than sitting inside one. */
const BLOCK_TAGS = 'div, p, ul, ol, li, blockquote, h1, h2, h3';

/** Serialise a contenteditable root into the markup subset. */
export function serializeToMarkup(root: HTMLElement): string {
  const lines: string[] = [];

  for (const node of Array.from(root.childNodes)) collectBlock(node, lines);

  return lines
    .join('\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

function collectBlock(node: Node, lines: string[]): void {
  if (node.nodeType === Node.TEXT_NODE) {
    // A bare text node is the first line of an editor nobody has pressed
    // Enter in yet; it belongs to whatever line is open.
    appendToLast(lines, node.textContent ?? '');
    return;
  }
  if (node.nodeType !== Node.ELEMENT_NODE) return;

  const el = node as HTMLElement;
  switch (el.tagName) {
    case 'UL':
    case 'OL': {
      const ordered = el.tagName === 'OL';
      let counter = 0;
      for (const item of Array.from(el.children)) {
        counter += 1;
        lines.push(`${ordered ? `${counter}.` : '-'} ${inlineOf(item)}`);
      }
      return;
    }
    case 'DIV':
    case 'P':
    case 'BLOCKQUOTE':
    case 'H1':
    case 'H2':
    case 'H3':
      // Browsers wrap each line in a div (or p) as Enter is pressed, but they
      // also nest those divs — a wrapper holding a run of line divs is normal
      // Chrome output. Flattening one of those to text would run every line it
      // holds into a single unbroken word, so anything carrying blocks of its
      // own is recursed into and only a leaf becomes a line.
      if (el.querySelector(BLOCK_TAGS)) {
        for (const child of Array.from(el.childNodes)) collectBlock(child, lines);
        return;
      }
      lines.push(inlineOf(el));
      return;
    case 'BR':
      lines.push('');
      return;
    default:
      appendToLast(lines, inlineOf(el));
  }
}

function appendToLast(lines: string[], text: string): void {
  if (text === '') return;
  if (lines.length === 0) lines.push(text);
  else lines[lines.length - 1] += text;
}

/** One block's contents as inline markup. */
function inlineOf(node: Node): string {
  if (node.nodeType === Node.TEXT_NODE) return node.textContent ?? '';
  if (node.nodeType !== Node.ELEMENT_NODE) return '';

  const el = node as HTMLElement;
  if (el.tagName === 'BR') return '\n';

  const inner = Array.from(el.childNodes).map(inlineOf).join('');
  // Marking whitespace produces tokens with nothing between them, which read as
  // literal characters once parsed back. Leave it alone.
  if (inner.trim() === '') return inner;

  switch (el.tagName) {
    case 'B':
    case 'STRONG':
      return wrap(inner, '**');
    case 'I':
    case 'EM':
      return wrap(inner, '_');
    case 'S':
    case 'STRIKE':
    case 'DEL':
      return wrap(inner, '~~');
    case 'A': {
      const href = el.getAttribute('href');
      return href ? `[${inner}](${href})` : inner;
    }
    default:
      return inner;
  }
}

/**
 * Wrap the words, not the spaces around them: `** bold **` is not a mark in any
 * markdown, and browsers hand back leading spaces inside a <b> all the time.
 */
function wrap(inner: string, token: string): string {
  const lead = inner.length - inner.trimStart().length;
  const tail = inner.length - inner.trimEnd().length;
  return `${inner.slice(0, lead)}${token}${inner.slice(lead, inner.length - tail)}${token}${inner.slice(inner.length - tail)}`;
}

/**
 * A draft written into the DOM the editor edits — `serializeToMarkup` backwards,
 * for the moment a one-line composer hands its half-typed message to the
 * full-height one.
 *
 * Nodes, never an HTML string. There is no escaping question to get wrong if
 * nothing is ever parsed as markup: a message containing "<b>hi" arrives as
 * those five characters, which is the same rule `RichText` follows for the
 * thread.
 *
 * The shapes are the ones a browser leaves behind itself as Enter is pressed —
 * a div per line, an empty div holding a <br> for a blank one, <b>/<i>/<s>
 * rather than <strong>/<em>/<del> — so a seeded draft and a typed one serialise
 * identically and `queryCommandState` lights the toolbar for both.
 *
 * The one rule that isn't obvious is the blank line between two paragraph runs.
 * `serializeToMarkup` joins every line with a single "\n", and a single "\n" is
 * what the parser reads as a line break *within* a paragraph — so without an
 * empty line between them, two paragraphs would come back as one paragraph of
 * two lines, and the round trip would quietly close a gap the writer left. No
 * separator anywhere else: a list already ends at the first line that isn't one.
 */
export function fillFromMarkup(root: HTMLElement, markup: string): void {
  const blocks = parseRichText(markup);
  const nodes: Node[] = [];

  blocks.forEach((block, index) => {
    if (block.kind === 'paragraph') {
      if (blocks[index - 1]?.kind === 'paragraph') nodes.push(lineOf([]));
      for (const line of block.lines) nodes.push(lineOf(line));
      return;
    }

    const list = document.createElement(block.ordered ? 'ol' : 'ul');
    for (const item of block.items) {
      const li = document.createElement('li');
      li.append(...inlineNodesOf(item));
      list.append(li);
    }
    nodes.push(list);
  });

  root.replaceChildren(...nodes);
}

function lineOf(inline: Inline[]): HTMLElement {
  const div = document.createElement('div');
  div.append(...inlineNodesOf(inline));
  return div;
}

function inlineNodesOf(inline: Inline[]): Node[] {
  // An empty line still needs a <br> to hold its height open — exactly what the
  // browser puts there when Enter is pressed on a line of its own. Without it
  // the div collapses and the blank line the writer left is invisible until
  // they type into it.
  if (inline.length === 0) return [document.createElement('br')];
  return inline.map(nodeOf);
}

function nodeOf(inline: Inline): Node {
  if (inline.kind === 'text') return document.createTextNode(inline.text);

  const el = document.createElement(TAGS[inline.kind]);
  el.append(...inline.children.map(nodeOf));
  if (inline.kind === 'link') el.setAttribute('href', inline.href);
  return el;
}

const TAGS = { bold: 'b', italic: 'i', strike: 's', link: 'a' } as const;
